// External dependencies
import { type FC } from 'react';
import { type Control, Controller, type FieldErrors } from 'react-hook-form-latest';
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { components, type CSSObjectWithLabel, type DropdownIndicatorProps } from 'react-select';

// Utilities
import { _isNotBlank } from '@/littledash';
import { decimalPlacesValidator, nilOrGreaterThan, nilOrGreaterThanOrEqualTo, valueRequired } from '@/validators';

// Model types
import type { TreatmentFormField, TreatmentFormUnit } from '@/components/Modals/AddTreatmentForm/TreatmentForm.model';

// Internal components
import { NumberInput as Number } from '@/components/UI/FormFields/Latest/Number';
import Icon from '@/components/UI/Icon';
import Select from '@/components/UI/Select';

// Relative imports
import Checkbox from '../Checkbox';
import './ControlledInputWithUnits.scss';

const DropdownIndicator = (count: number) => (props: DropdownIndicatorProps<TreatmentFormUnit>) =>
  count > 1 ? (
    <components.DropdownIndicator {...props}>
      <Icon icon="down_triangle" width="8" height="8" className="right-1 blue" style={{ pointerEvents: 'none' }} />
    </components.DropdownIndicator>
  ) : null;

interface ControlledInputWithUnitsProps {
  id: string;
  field: TreatmentFormField;
  disabled: boolean;
  hasError: boolean;
  classes: string;
  units: Array<TreatmentFormUnit>;
  /** FieldValues is intentional — field names are built dynamically (e.g. `${id}.default_value`) */
  control: Control;
  errors: FieldErrors;
  selectRequired?: boolean;
  unitsDisabled?: boolean;
  tooltip?: string;
  testId?: string;
  allowZero?: boolean;
}

const ControlledInputWithUnits: FC<ControlledInputWithUnitsProps> = ({
  id,
  field,
  disabled = false,
  hasError = false,
  classes,
  units,
  control,
  errors,
  selectRequired,
  unitsDisabled,
  tooltip,
  testId = 'controlled-input-with-units',
  allowZero = false,
}) => (
  <div data-test-component="ControlledInputWithUnits" data-testid={testId}>
    <div className={`flex relative ${classes}`}>
      <Controller
        control={control}
        rules={{
          required: field.required ? `${field.label} is required` : undefined,
          validate: {
            valueRequired: valueRequired(field.label, field.required),
            decimalPlacesValidator: decimalPlacesValidator(field.label),
            greaterThan: allowZero
              ? nilOrGreaterThanOrEqualTo(field.label, 0, field.required)
              : nilOrGreaterThan(field.label, 0, field.required),
          },
        }}
        name={`${id}.default_value`}
        render={({ field: { value, onChange } }) => (
          <Number
            value={value}
            onChange={onChange}
            disabled={disabled}
            style={{ paddingRight: '3.5rem' }}
            className={hasError ? 'input__error' : ''}
          />
        )}
      />

      <div className={cn('pointer', 'unitSelection', selectRequired === true ? 'customUnitWidth' : 'unitWidth')}>
        <Controller
          name={`${id}.unit`}
          control={control}
          rules={{
            required: 'Unit is required',
          }}
          render={({ field: { value, onChange } }) => (
            <Select
              value={value}
              options={units}
              onChange={onChange}
              getOptionLabel={(option: TreatmentFormUnit) => option.name}
              getOptionValue={(option: TreatmentFormUnit) => option.id.toString()}
              customStyles={{
                indicatorsContainer: () => ({
                  border: 'none',
                  padding: 0,
                }),
                singleValue: (base: CSSObjectWithLabel) => ({
                  ...base,
                  color: unitsDisabled ? '#d3d3d3' : '#0647a6',
                  fontSize: '80%',
                  textAlign: 'end',
                }),
              }}
              className="w-100 mb3"
              components={{
                DropdownIndicator: (props: DropdownIndicatorProps<TreatmentFormUnit>) =>
                  DropdownIndicator(units.length)(props),
              }}
              isSearchable={false}
              disabled={unitsDisabled}
              isMulti={false}
            />
          )}
        />
      </div>
    </div>
    {selectRequired === true && (
      <div className="pb3 flex justify-between" data-test-element="requied-checkbox-container" data-test-key={id}>
        <Controller
          name={`${id}.required`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox label="Required" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
          )}
        />
        {_isNotBlank(tooltip) && (
          <span data-tooltip-id="global-tooltip-id">
            <Icon className="mid-gray hover-blue" icon="info" width="24" height="24" tooltip={tooltip} />
          </span>
        )}
      </div>
    )}
    <div className="flex flex-column">
      <ErrorMessage
        name={`${id}.default_value`}
        errors={errors}
        render={({ message }) => <small className="red mb3">{message}</small>}
      />
      <ErrorMessage
        name={`${id}.unit`}
        errors={errors}
        render={({ message }) => <small className="red mb3">{message}</small>}
      />
    </div>
  </div>
);
export default ControlledInputWithUnits;
